import * as yup from "yup";
import { checkUrl, checkHtml } from "../../../utils/helper";

// Organization validation schema
export const organizationSchema = yup.object().shape({
  organizationName: yup
    .string()
    .required("Organization name is required")
    .transform((value) => (typeof value === 'string' ? value.trim() : value))
    .min(1, "Organization name cannot be empty")
    .max(64, "Organization name cannot exceed 64 characters")
    .test(
      "no-url",
      "Organization name cannot contain URLs",
      (value) => !checkUrl(value)
    )
    .test("no-html", "Invalid organization name", (value) => !checkHtml(value)),
});

// Team setup validation schema
export const teamSetupSchema = yup.object().shape({
  teamSize: yup.string().required("Please select your team size"),

  workTypes: yup
    .array()
    .min(1, "Please select at least one work type")
    .required("Please select your work type"),

  customIndustry: yup.string().when("workTypes", {
    is: (workTypes) => workTypes && workTypes.includes("others"),
    then: (schema) =>
      schema
        .required("Please specify your industry")
        .transform((value) => (typeof value === 'string' ? value.trim() : value))
        .min(3, "Industry name is too short")
        .max(128, "Industry name cannot exceed 128 characters")
        .test(
          "no-url",
          "Industry name cannot contain URLs",
          (value) => !checkUrl(value)
        )
        .test("no-html", "Invalid industry name", (value) => !checkHtml(value)),
    otherwise: (schema) => schema.notRequired(),
  }),
});

// Invite team validation schema
export const inviteTeamSchema = yup.object().shape({
  inviteEmails: yup
    .array()
    .of(
      yup
        .string()
        .email("Please enter a valid email address")
        .required("Email is required")
    )
    .max(5, "Maximum 5 invitations allowed")
    .test(
      "no-duplicates",
      "Duplicate email addresses are not allowed",
      function (emails) {
        if (!emails) return true;
        const uniqueEmails = new Set(emails);
        return uniqueEmails.size === emails.length;
      }
    ),

  skipInvite: yup.boolean(),
});

// Email validation helper
export const validateEmailList = (emailString) => {
  const stringEmailString = emailString ? String(emailString) : "";
  if (!stringEmailString || stringEmailString.trim() === "") {
    return { valid: [], invalid: [], error: "Please enter at least one email" };
  }

  const emails = stringEmailString
    .split(/[,;\s]+/)
    .map((email) => String(email).trim())
    .filter((email) => email.length > 0);

  const validEmails = [];
  const invalidEmails = [];

  emails.forEach((email) => {
    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      validEmails.push(email);
    } else {
      invalidEmails.push(email);
    }
  });

  return {
    valid: validEmails,
    invalid: invalidEmails,
    error: invalidEmails.length > 0 ? "Some emails are invalid" : null,
  };
};

// Generic text field validation (for custom inputs)
export const createTextFieldSchema = (fieldName, options = {}) => {
  const {
    required = true,
    minLength = 2,
    maxLength = 100,
    requiredMessage = `${fieldName} is required`,
    minLengthMessage = `${fieldName} must be at least ${minLength} characters`,
    maxLengthMessage = `${fieldName} cannot exceed ${maxLength} characters`,
  } = options;

  let schema = yup.string();

  if (required) {
    schema = schema.required(requiredMessage);
  }

  return schema
    .transform((value) => (typeof value === 'string' ? value.trim() : value))
    .min(minLength, minLengthMessage)
    .max(maxLength, maxLengthMessage)
    .test(
      "no-url",
      `${fieldName} cannot contain URLs`,
      (value) => !value || !checkUrl(value)
    )
    .test(
      "no-html",
      `Invalid ${fieldName}`,
      (value) => !value || !checkHtml(value)
    );
};

// Organization name validation with smart suggestions
export const validateOrganizationName = (value) => {
  const errors = [];

  if (!value || value.trim() === "") {
    errors.push("Organization name cannot be empty");
  } else {
    const trimmed = value.trim();

    if (trimmed.length > 64) {
      errors.push("Organization name cannot exceed 64 characters");
    }

    if (checkUrl(trimmed)) {
      errors.push("Organization name cannot contain URLs");
    }

    if (checkHtml(trimmed)) {
      errors.push("Invalid organization name");
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

// Custom industry validation
export const validateCustomIndustry = (value) => {
  const errors = [];

  if (!value || value.trim() === "") {
    errors.push("Please specify your industry");
  } else {
    const trimmed = value.trim();

    if (trimmed.length < 3) {
      errors.push("Industry name is too short");
    }

    if (trimmed.length > 128) {
      errors.push("Industry cannot exceed 128 characters");
    }

    if (checkUrl(trimmed)) {
      errors.push("Industry cannot contain URLs");
    }

    if (checkHtml(trimmed)) {
      errors.push("Invalid industry");
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};
